import { initLogrocket } from "@bestowinc/logrocket";
import {
  createNewRelicBrowserTransport,
  setTelemetryTransport,
  type Scalar,
  type TelemetryTransport,
} from "@bestowinc/logger/client";
import type { SerializedBrowserTelemetryConfig } from "@bestowinc/tinkertoy-bestow-types";
import {
  Agent,
  GenericEvents,
  JSErrors,
  Logging,
  PageViewEvent,
} from "@newrelic/browser-agent";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";

const TELEMETRY_INITIALIZED_KEY = Symbol.for(
  "@bestowinc/tinkertoy-shell:telemetry-initialized",
);

function hasConfiguredVendors(
  config: SerializedBrowserTelemetryConfig,
): boolean {
  return config.newRelic !== undefined || config.logRocket !== undefined;
}

function initNewRelicAgent(
  newRelicConfig: NonNullable<SerializedBrowserTelemetryConfig["newRelic"]>,
): void {
  new Agent({
    init: newRelicConfig.init,
    info: newRelicConfig.info,
    loader_config: newRelicConfig.loader_config,
    features: [Logging, JSErrors, GenericEvents, PageViewEvent],
  });
}

function buildCompositeTransport(options: {
  newRelicEnabled: boolean;
  logRocketEnabled: boolean;
}): TelemetryTransport | null {
  const newRelicTransport = options.newRelicEnabled
    ? createNewRelicBrowserTransport()
    : null;
  const captureException = options.logRocketEnabled
    ? (error: Error, metadata: Record<string, Scalar>) => {
        LogRocket.captureException(error, { extra: metadata });
      }
    : undefined;

  if (!newRelicTransport && !captureException) {
    return null;
  }

  if (newRelicTransport) {
    return {
      log: newRelicTransport.log.bind(newRelicTransport),
      noticeError: newRelicTransport.noticeError.bind(newRelicTransport),
      ...(newRelicTransport.recordMetric
        ? { recordMetric: newRelicTransport.recordMetric.bind(newRelicTransport) }
        : {}),
      ...(newRelicTransport.getCorrelationMetadata
        ? {
            getCorrelationMetadata:
              newRelicTransport.getCorrelationMetadata.bind(newRelicTransport),
          }
        : {}),
      ...(captureException ? { captureException } : {}),
    };
  }

  return {
    log: () => undefined,
    noticeError: () => undefined,
    ...(captureException ? { captureException } : {}),
  };
}

export function initializeBrowserTelemetry(
  config?: SerializedBrowserTelemetryConfig,
): void {
  if (Reflect.get(globalThis, TELEMETRY_INITIALIZED_KEY) === true) {
    return;
  }
  Reflect.set(globalThis, TELEMETRY_INITIALIZED_KEY, true);

  if (config === undefined) {
    return;
  }

  if (!hasConfiguredVendors(config)) {
    setTelemetryTransport(null);
    return;
  }

  let newRelicInitialized = false;
  let logRocketInitialized = false;

  if (config.newRelic) {
    try {
      initNewRelicAgent(config.newRelic);
      newRelicInitialized = true;
    } catch (error) {
      console.warn(
        "[tinkertoy-shell:telemetry] New Relic initialization failed",
        error,
      );
    }
  }

  if (config.logRocket) {
    try {
      initLogrocket(config.logRocket.projectId);
      // logrocket-react v7 registers a global click listener; it no longer accepts
      // a LogRocket instance (legacy entrypoints still pass one for older versions).
      setupLogRocketReact();
      logRocketInitialized = true;
    } catch (error) {
      console.warn(
        "[tinkertoy-shell:telemetry] LogRocket initialization failed",
        error,
      );
    }
  }

  const composite = buildCompositeTransport({
    newRelicEnabled: newRelicInitialized,
    logRocketEnabled: logRocketInitialized,
  });

  if (composite) {
    setTelemetryTransport(composite);
    return;
  }

  setTelemetryTransport(null);
}
